import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { AppMenuitem } from './app.menuitem';

@Component({
    selector: 'app-menu',
    standalone: true,
    imports: [CommonModule, AppMenuitem, RouterModule],
    template: `<ul class="layout-menu">
        <ng-container *ngFor="let item of model; let i = index">
            <li app-menuitem *ngIf="!item.separator" [item]="item" [index]="i" [root]="true"></li>
            <li *ngIf="item.separator" class="menu-separator"></li>
        </ng-container>
    </ul> `
})
export class AppMenu implements OnInit, OnDestroy {
    model: MenuItem[] = [];

    private translate = inject(TranslateService);
    private langChangeSubscription?: Subscription;

    ngOnInit() {
        // Build menu initially
        this.buildMenu();

        // Rebuild menu when language changes
        this.langChangeSubscription = this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
            this.buildMenu();
        });
    }

    ngOnDestroy() {
        // Clean up subscription
        this.langChangeSubscription?.unsubscribe();
    }

    private buildMenu() {
        this.model = [
            {
                label: this.translate.instant('menu.home'),
                items: [{ label: this.translate.instant('menu.dashboard'), icon: 'pi pi-fw pi-home', routerLink: ['/dashboard'] }]
            },
            {
                label: this.translate.instant('menu.operations'),
                items: [
                    {
                        label: this.translate.instant('menu.orders'),
                        icon: 'pi pi-fw pi-file-edit',
                        items: [
                            { label: this.translate.instant('menu.viewOrders'), icon: 'pi pi-fw pi-list', routerLink: ['/order'] },
                            { label: this.translate.instant('menu.createOrder'), icon: 'pi pi-fw pi-plus', routerLink: ['/order/create'] }
                        ]
                    },
                    {
                        label: this.translate.instant('menu.vehicleAllocation'),
                        icon: 'pi pi-fw pi-truck',
                        items: [
                            { label: this.translate.instant('menu.viewAllocations'), icon: 'pi pi-fw pi-list', routerLink: ['/vehicle-allocation'] },
                            { label: this.translate.instant('menu.createAllocation'), icon: 'pi pi-fw pi-plus', routerLink: ['/vehicle-allocation/create'] }
                        ]
                    },
                    {
                        label: this.translate.instant('menu.deliveryManagement'),
                        icon: 'pi pi-fw pi-send',
                        routerLink: ['/delivery-management']
                    },
                    {
                        label: this.translate.instant('menu.shipperPayments'),
                        icon: 'pi pi-fw pi-wallet',
                        routerLink: ['/shipper-payments']
                    }
                ]
            },
            {
                label: this.translate.instant('menu.pickups'),
                items: [
                    {
                        label: this.translate.instant('menu.pickupVouchers'),
                        icon: 'pi pi-fw pi-receipt',
                        items: [
                            { label: this.translate.instant('menu.viewVouchers'), icon: 'pi pi-fw pi-list', routerLink: ['/pickup-vouchers'] },
                            { label: this.translate.instant('menu.createVoucher'), icon: 'pi pi-fw pi-plus', routerLink: ['/pickup-vouchers/create'] }
                        ]
                    },
                    {
                        label: this.translate.instant('menu.pickupAllocation'),
                        icon: 'pi pi-fw pi-calendar-plus',
                        items: [
                            { label: this.translate.instant('menu.viewAllocations'), icon: 'pi pi-fw pi-list', routerLink: ['/pickup-allocations'] },
                            { label: this.translate.instant('menu.createAllocation'), icon: 'pi pi-fw pi-plus', routerLink: ['/pickup-allocations/create'] }
                        ]
                    }
                ]
            },
            {
                label: this.translate.instant('menu.payments'),
                items: [
                    {
                        label: this.translate.instant('menu.paymentManagement'),
                        icon: 'pi pi-fw pi-money-bill',
                        routerLink: ['/payment-management']
                    }
                ]
            },
            {
                label: this.translate.instant('menu.sales'),
                items: [
                    {
                        label: this.translate.instant('menu.employees'),
                        icon: 'pi pi-fw pi-users',
                        routerLink: ['/employee-master']
                    },
                    {
                        label: this.translate.instant('menu.shippers'),
                        icon: 'pi pi-fw pi-user',
                        routerLink: ['/shippers']
                    }
                ]
            },
            {
                label: this.translate.instant('menu.masterData'),
                items: [
                    {
                        label: this.translate.instant('menu.companyMaster'),
                        icon: 'pi pi-fw pi-building',
                        routerLink: ['/company']
                    },
                    {
                        label: this.translate.instant('menu.vehicleMaster'),
                        icon: 'pi pi-fw pi-car',
                        routerLink: ['/settings/vehicle']
                    },
                    {
                        label: this.translate.instant('menu.geofencing'),
                        icon: 'pi pi-fw pi-map-marker',
                        items: [
                            { label: this.translate.instant('menu.countries'), icon: 'pi pi-fw pi-globe', routerLink: ['/settings/countries'] },
                            { label: this.translate.instant('menu.regions'), icon: 'pi pi-fw pi-map', routerLink: ['/settings/regions'] },
                            { label: this.translate.instant('menu.zones'), icon: 'pi pi-fw pi-th-large', routerLink: ['/settings/zones'] },
                            { label: this.translate.instant('menu.locations'), icon: 'pi pi-fw pi-map-marker', routerLink: ['/settings/locations'] }
                        ]
                    }
                ]
            },
            {
                label: this.translate.instant('menu.security'),
                items: [
                    {
                        label: this.translate.instant('menu.roleManagement'),
                        icon: 'pi pi-fw pi-shield',
                        routerLink: ['/role']
                    }
                ]
            },
            {
                label: this.translate.instant('menu.utilities'),
                items: [
                    {
                        label: this.translate.instant('menu.mapView'),
                        icon: 'pi pi-fw pi-map',
                        routerLink: ['/map']
                    }
                ]
            }
        ];
    }
}
